import React from 'react';
import ServicePageLayout from '../../components/seo/ServicePageLayout';
import { getGallery } from '../../seo/galleryConfig';

const faqs = [
  {
    q: 'My tap drips even when fully closed — is that just a washer?',
    a: 'For traditional pillar taps, yes — almost always a worn rubber washer or ceramic disc cartridge. The fix is usually 20 minutes and a few-pence part. For modern monobloc mixers, the cartridge inside is the equivalent and is also a quick swap. Pretty much every drip we see is a £40-£70 fix.'
  },
  {
    q: 'My tap has gone stiff — is it about to fail?',
    a: 'Usually it just needs the spindle cleaning and re-greasing, or the cartridge replacing. Stiffness is a sign that mineral buildup or limescale is starting to bind the moving parts. Catching it now prevents the inevitable harder-to-fix snap-off later.'
  },
  {
    q: 'Can you fit a new kitchen mixer tap?',
    a: 'Absolutely — that\'s one of our most common jobs. We disconnect and remove the old tap, clean up the worktop opening, fit the new mixer (with new flexible connectors and isolation valves), and test for leaks. Typical install time: 45 minutes to 1 hour.'
  },
  {
    q: 'My outdoor garden tap is leaking — same job?',
    a: 'Similar approach but we also check the wall mount and the internal isolator. Many outdoor taps date from before non-return-valve regulations and may need a small compliance upgrade as part of the repair.'
  },
  {
    q: 'How long do modern taps last?',
    a: 'A decent-quality kitchen mixer typically lasts 8-15 years before the cartridge needs replacing or the chrome plating starts failing. Cheap supermarket taps often need attention within 2-3 years — which is why we recommend mid-range brands when supplying.'
  },
];

const relatedServices = [
  { slug: 'plumbing', label: 'Plumbing Services', blurb: 'Full domestic plumbing repairs and upgrades.' },
  { slug: 'kitchen-plumbing', label: 'Kitchen Plumbing', blurb: 'Coming soon — kitchen sink, dishwasher, washing-machine plumbing.' },
  { slug: 'bathroom-plumbing', label: 'Bathroom Plumbing', blurb: 'Coming soon — bathroom plumbing repairs and installs.' },
];

const TapRepairsPage = () => (
  <ServicePageLayout
    seoTitle="Tap Repairs & Tap Replacement Services in Kent, London & Essex | H.A.R.P Solutions LTD"
    seoDescription="Tap repair and replacement services across Kent, London and Essex. Dripping taps, stiff handles, new kitchen and bathroom taps, outdoor garden taps. Call +44 330 122 1094."
    canonicalPath="/services/tap-repairs"
    keywords="dripping tap repair, tap replacement, leaking tap, kitchen tap, bathroom tap, mixer tap, tap repair Kent, tap repair London, tap repair Essex"
    pageTitle="Tap Repairs &amp; Replacements — Fast, Tidy, Done Right"
    serviceName="Tap Repairs &amp; Replacements"
    serviceSlug="tap-repairs"
    heroSubheading="Stop the drip. Save the water. Restore the bathroom or kitchen."
    intro="A dripping tap loses around 3-5 litres of water a day — that's over 1,200 litres a year per tap, often more on a hot-water drip because of the wasted heating cost. Beyond the bill, a constant drip stains ceramic, damages chrome and is a daily irritation. We repair and replace every kind of tap across Kent, London and Essex — usually in under an hour."
    bullets={[
      'Worn washer, cartridge and ceramic-disc repairs',
      'New kitchen and bathroom mixer installations',
      'Monobloc, pillar, deck-mounted and wall-mounted',
      'Outdoor garden tap repair and compliance upgrades',
      'Isolation-valve fitting for future-proof maintenance',
      'Limescale-friendly cartridges in hard-water areas',
    ]}
    faqs={faqs}
    relatedServices={relatedServices}
    galleryImages={getGallery('tap-repairs')}
  >
    <h2>Why Even A Slow Drip Matters</h2>
    <p>
      A "harmless" drip is anything but. Hot-water drips waste both water and the gas/electric that heated it. Ceramic basins and stainless
      sinks develop hard-to-remove staining at the drip point. The constant trickle promotes limescale buildup on the tap body, which
      eventually seizes the moving parts and turns a 20-minute repair into a full replacement. Catching drips early saves money in every
      direction.
    </p>

    <h2>Common Tap Issues We Repair</h2>
    <h3>Dripping when closed</h3>
    <p>
      For traditional taps: worn rubber washer. For modern monobloc and ceramic-disc taps: a worn cartridge. Either way, the
      replacement part is inexpensive — most of the cost is our visit time. We carry common cartridges and washers on the van.
    </p>
    <h3>Stiff or seized handles</h3>
    <p>
      Limescale and mineral buildup binding the moving parts. We strip the tap, descale, regrease, and either refit (if the parts are
      sound) or replace the cartridge. Avoids the worst-case scenario where the spindle eventually snaps off in your hand.
    </p>
    <h3>Loose tap at base</h3>
    <p>
      The underside backnut has worked loose, the seal underneath has perished, or the worktop has flexed. We re-secure, re-seal with a
      modern silicone-based gasket, and check that the supply pipework underneath isn't being stressed by the movement.
    </p>
    <h3>Low or unbalanced flow</h3>
    <p>
      Often a blocked aerator (the mesh at the end of the spout) full of limescale — clears in 10 minutes. Sometimes a partially closed
      isolation valve, sometimes a more systemic supply-pressure issue. We diagnose properly rather than just blaming the tap.
    </p>

    <h2>New Tap Installation</h2>
    <p>
      Replacing a tired old tap with a modern mixer instantly modernises a kitchen or bathroom. We supply quality mid-range and premium
      brands (Bristan, Methven, Grohe, Hansgrohe, etc.) or fit a customer-supplied tap if you've already chosen one. Every install
      includes:
    </p>
    <ul>
      <li>New flexible tap-tail hoses (we don't reuse old ones).</li>
      <li>Isolation valves below the tap — so the next maintenance job is much easier.</li>
      <li>Proper limescale-resistant sealant where needed.</li>
      <li>Leak test under pressure before we leave.</li>
    </ul>

    <h2>Outdoor Taps</h2>
    <p>
      External taps deserve special care: they need a non-return valve under current water regulations, they need protection from frost
      (drain-and-isolate before winter), and they need to be securely fixed to brickwork rather than just to a thin cladding panel. We
      replace and upgrade outdoor taps with all required compliance built in.
    </p>

    <h2>Book A Visit</h2>
    <p>
      Call <a href="tel:+443301221094"><strong>+44 330 122 1094</strong></a> for a quick estimate, or send a photo via WhatsApp using the
      green button — we can almost always price a tap repair from a single picture. For wider plumbing work see our{' '}
      <a href="/services/plumbing">plumbing services</a>.
    </p>
  </ServicePageLayout>
);

export default TapRepairsPage;
